import React, { useMemo } from 'react';
import GoogleMapReact from 'google-map-react';

import { Grid, Button, Paper, Typography, Box } from '@mui/material';
import HomeSharpIcon from '@mui/icons-material/HomeSharp';

import HeroTitleSection from '../IndividualListingModal/HeroTitleSection';
import Ameneties from '../IndividualListingModal/Ameneties';
import styles from '../IndividualListingModal/style';
import { mapOptions, COLOR_PRIMARY_DARK, COLOR_PRIMARY_LIGHT, POINTER_GREEN } from 'src/const';
import { calculateSmartDistance, getFuture30DaysKeyMetrics } from 'src/utils/functions';
import '../../HOC/Pointer/Pointer.scss';
import { Listing } from 'src/interfaces/idashboard';

const Marker = ({ lat, lng, isUserListing }) => {
  return (
    <>
      {isUserListing ? (
        <HomeSharpIcon
          sx={{
            fontSize: 40,
            color: COLOR_PRIMARY_DARK,
            cursor: 'pointer',
          }}
        />
      ) : (
        <div className="pointer-compset-active"></div>
      )}
    </>
  );
};

const Future30DaysKeyMetricsBox = ({ title, valueString }) => {
  return (
    <Grid item>
      <Box
        sx={{
          height: 100,
          width: 150,
          '&:hover': {
            border: `15px solid ${COLOR_PRIMARY_LIGHT}`,
          },
          borderRadius: '2px',
          border: `15px solid ${COLOR_PRIMARY_LIGHT}30`,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography
          align="center"
          variant="h5"
          sx={{
            color: POINTER_GREEN,
            fontWeight: 'bold',
          }}
        >
          {valueString}
        </Typography>
        <Typography color="textSecondary" variant="body1" align="center">
          {title}
        </Typography>
      </Box>
    </Grid>
  );
};

const ListingDetailsView = ({ listing, userListing, isMobileScreen, setCalendarOpen, rates }) => {
  const listingCoordinate = {
    lat: listing.latitude,
    lng: listing.longitude,
  };
  const userListingCoordinate = {
    lat: userListing.latitude,
    lng: userListing.longitude,
  };
  const distance: string = calculateSmartDistance(listingCoordinate, userListingCoordinate);
  const {
    avgDailyRateNext30Days,
    occupancyRateNext30Days,
    avgAvailableDailyRateNext30Days,
    avgBookedDailyRateNext30Days,
  } = getFuture30DaysKeyMetrics(rates);
  const revParNext30Days = Math.round((avgDailyRateNext30Days * occupancyRateNext30Days) / 100);

  const apiIsLoaded = (map, maps, listing: Listing, userListing) => {
    const bounds = new maps.LatLngBounds();
    bounds.extend(new maps.LatLng(listing.latitude, listing.longitude));
    bounds.extend(new maps.LatLng(userListing.latitude, userListing.longitude));
    map.fitBounds(bounds);
  };

  let compListingMarker = useMemo(() => {
    return <Marker lat={listing.latitude} lng={listing.longitude} isUserListing={false} />;
  }, []);

  let userListingMarker = useMemo(() => {
    return <Marker lat={userListing.latitude} lng={userListing.longitude} isUserListing={true} />;
  }, []);

  return (
    <>
      <Grid
        item
        xs={12}
        container
        direction="row"
        justifyContent="center"
        sx={{
          width: isMobileScreen ? '98%' : '100%',
        }}
        spacing={2}
      >
        <Grid item md={6} sm={12} xs={12} spacing={2} container justifyContent="flex-start">
          <Grid item xs={12}>
            <HeroTitleSection data={listing} isCompListing={true} />
          </Grid>

          <Grid item container xs={12}>
            <Typography
              color="textPrimary"
              variant="subtitle2"
              sx={{ fontSize: '16px', marginBottom: '10px' }}
            >
              Future 30 days key insights
            </Typography>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="flex-start"
              spacing={2}
              item
              xs={12}
            >
              <Future30DaysKeyMetricsBox title="RevPAR" valueString={`$${revParNext30Days}`} />
              <Future30DaysKeyMetricsBox
                title="Occupancy Rate"
                valueString={`${occupancyRateNext30Days}%`}
              />
              <Future30DaysKeyMetricsBox
                title="Available Daily Rate (average)"
                valueString={`$${avgAvailableDailyRateNext30Days}`}
              />
              <Future30DaysKeyMetricsBox
                title="Booked Daily Rate (average)"
                valueString={`$${avgBookedDailyRateNext30Days}`}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item md={6} sm={12} xs={12} spacing={2} container justifyContent="flex-start">
          <Grid item xs={12}>
            <Paper
              sx={{
                height: 250,
                width: '100%',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <GoogleMapReact
                bootstrapURLKeys={{
                  key: 'AIzaSyDUnmEGqix9gziZQjtCQFJ7mCTSDV9yPDk',
                  libraries: ['drawing', 'places', 'visualization'],
                  id: 'dynamic-pricing-map',
                }}
                center={{ lat: listing.latitude, lng: listing.longitude }}
                options={mapOptions}
                zoom={17}
                yesIWantToUseGoogleMapApiInternals
                onGoogleApiLoaded={({ map, maps }) => apiIsLoaded(map, maps, listing, userListing)}
              >
                {compListingMarker}
                {userListingMarker}
              </GoogleMapReact>
            </Paper>
            <Typography
              sx={{
                color: COLOR_PRIMARY_DARK,
                textDecoration: 'underline',
                fontWeight: 500,
                marginTop: '10px',
              }}
              variant="body2"
            >
              Located at {distance} from your listing
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Ameneties data={listing} isMobileScreen={isMobileScreen} />
          </Grid>

          <Grid item xs={12}>
            <Button
              color="primary"
              onClick={() => {
                setCalendarOpen(true);
              }}
              sx={styles.button}
            >
              Check Calendar
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default ListingDetailsView;
